import {BorderBox, Flex, Link, Text} from '@primer/components'
import {ChevronDownIcon, ChevronUpIcon, XIcon} from '@primer/octicons-react'
import {Link as GatsbyLink} from 'gatsby'
import debounce from 'lodash.debounce'
import React from 'react'
import navItems from '@primer/gatsby-theme-doctocat/src/nav.yml'
import primerNavItems from '@primer/gatsby-theme-doctocat/src/primer-nav.yml'
import useSiteMetadata from '@primer/gatsby-theme-doctocat/src/use-site-metadata'
import DarkButton from '@primer/gatsby-theme-doctocat/src/components/dark-button'
import Details from '@primer/gatsby-theme-doctocat/src/components/details'
import Drawer from '@primer/gatsby-theme-doctocat/src/components/drawer'
import NavItems from '@primer/gatsby-theme-doctocat/src/components/nav-items'

export function useNavDrawerState(breakpoint) {
  // Handle string values from themes with units at the end
  if (typeof breakpoint === 'string') {
    breakpoint = parseInt(breakpoint, 10)
  }
  const [isOpen, setOpen] = React.useState(false)

  const onResize = React.useCallback(() => {
    if (window.innerWidth >= breakpoint) {
      setOpen(false)
    }
  }, [breakpoint, setOpen])

  const debouncedOnResize = React.useCallback(debounce(onResize, 250), [
    onResize,
  ])

  React.useEffect(() => {
    if (isOpen) {
      window.addEventListener('resize', debouncedOnResize)
      return () => {
        // cancel any debounced invocation of the resize handler
        debouncedOnResize.cancel()
        window.removeEventListener('resize', debouncedOnResize)
      }
    }
  }, [isOpen, debouncedOnResize])

  return [isOpen, setOpen]
}

function NavDrawer({isOpen, onDismiss}) {
  const siteMetadata = useSiteMetadata()
  return (
    <Drawer isOpen={isOpen} onDismiss={onDismiss}>
      <Flex
        flexDirection="column"
        height="100%"
        bg="gray.9"
        style={{overflow: 'auto', WebkitOverflowScrolling: 'touch'}}
      >
        <Flex flexDirection="column" flex="0 0 auto" color="blue.2" bg="gray.9">
          <BorderBox
            borderWidth={0}
            borderRadius={0}
            borderBottomWidth={1}
            borderColor="gray.7"
          >
            <Flex
              py={3}
              pl={4}
              pr={3}
              alignItems="center"
              justifyContent="space-between"
            >
              <Link as={GatsbyLink} to="/" color="rei.pink" fontFamily="mono">
                {siteMetadata.shortName}
              </Link>
              <DarkButton aria-label="Close" onClick={onDismiss}>
                <XIcon />
              </DarkButton>
            </Flex>
          </BorderBox>
          <Flex flexDirection="column">
            <PrimerNavItems items={primerNavItems} />
          </Flex>
        </Flex>
        {navItems.length > 0 ? (
          <Flex
            flexDirection="column"
            flex="1 0 auto"
            color="gray.7"
            bg="gray.0"
          >
            <NavItems items={navItems} />
          </Flex>
        ) : null}
      </Flex>
    </Drawer>
  )
}

function PrimerNavItems({items}) {
  return items.map((item, index) => {
    return (
      <BorderBox
        key={item.title}
        borderWidth={0}
        borderRadius={0}
        borderTopWidth={index !== 0 ? 1 : 0}
        borderColor="gray.7"
        p={4}
      >
        {item.children ? (
          <Details key={index}>
            {({open, toggle}) => (
              <>
                <summary onClick={toggle} style={{cursor: 'pointer'}}>
                  <Flex alignItems="center" justifyContent="space-between">
                    <Text>{item.title}</Text>
                    {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </Flex>
                </summary>
                <Flex flexDirection="column" mt={2}>
                  {item.children.map((child) => (
                    <Link
                      key={child.title}
                      href={child.url}
                      py={1}
                      mt={2}
                      fontSize={1}
                      color="inherit"
                    >
                      {child.title}
                    </Link>
                  ))}
                </Flex>
              </>
            )}
          </Details>
        ) : (
          <Link key={index} href={item.url} color="inherit" display="block">
            {item.title}
          </Link>
        )}
      </BorderBox>
    )
  })
}

export default NavDrawer
